<script lang="ts">
  import HelpTooltip from "@/Common/HelpTooltip.svelte";
  import { Button } from "@/lib/components/ui/button";
  import { Skeleton } from "@/lib/components/ui/skeleton";
  import { cn } from "@/lib/utils";
  import { convertDataToXSSTargetsResponse, postFreshnessFeedback, type XSSTargetsResponse } from "@/Util/api";
  import { round } from "@/Util/utils";
  import { getContext } from "svelte";
  import type { MyFitnessStores } from "../stores";

  export let xssTargetsResponse: XSSTargetsResponse;
  // For a past day, the backend anchors this to the end of that day's last activity rather than
  // a live "now" — so the heading reflects what's actually being shown instead of implying it's live.
  export let isViewingToday: boolean = true;
  const { trainingAdvice, trainingPlan } = getContext<MyFitnessStores>('myFitness');

  let isLoading = false;

  let recoveryDays: {
    low: number;
    high: number;
    peak: number;
  } | null;
  $: recoveryDays = xssTargetsResponse ? xssTargetsResponse.recommendedRecoveryDays : null;

  let selectedFreshness: 'Very fresh' | 'Fresh' | 'Tired' | 'Very tired' | 'Detraining' | null = null;
  $: selectedFreshness = $trainingAdvice?.form_cat ? $trainingAdvice.form_cat : null;

  $: formattedRecoveryTime = recoveryDays ? {
    low: formatRecoveryTime(recoveryDays.low),
    high: formatRecoveryTime(recoveryDays.high),
    peak: formatRecoveryTime(recoveryDays.peak),
  } : null;

  function formatRecoveryTime(days: number): { value: number; unit: string } {
    if (!days) return { value: 0, unit: 'hours' };
    if (days < 2) {
      const hours = days * 24;
      if (round(hours) < 1) return { value: 0, unit: 'hours' };
      return { value: round(hours), unit: `hour${hours >= 2 ? 's' : ''}` };
    } else {
      return { value: round(days), unit: `day${days >= 2 ? 's' : ''}` };
    }
  }

  function getRecoveryColour(status: string) {
    switch (status) {
      case 'Very fresh':
        return 'text-green-600';
      case 'Fresh':
        return 'text-blue-600';
      case 'Tired':
        return 'text-yellow-500';
      case 'Very tired':
        return 'text-red-600';
      case 'Detraining':
        return 'text-yellow-800'; // brown
      default:
        return '';
    }
  }

  function getRecoveryMessage(status: string, isOverride: boolean) {
    let message = '. ';
    if (isOverride) {
      message = ' until tomorrow or until your next activity is processed. ';
    }
    switch (status) {
      case 'Very fresh':
        return message + 'Train as usual.';
      case 'Fresh':
        return message + 'Train as usual.';
      case 'Tired':
        return message + 'Endurance training recommended.';
      case 'Very tired':
        return message + 'Rest recommended.';
      case 'Detraining':
        return message + 'Train as usual.';
      default:
        return '';
    }
  }

  async function updateFreshness(feedback: 'Very fresh' | 'Fresh' | 'Tired' | 'Very tired' | null) {
    selectedFreshness = feedback;
    isLoading = true;
    await postFreshnessFeedback(feedback).then(data => {
      if (data.advice) trainingAdvice.set(data.advice)
      if (data.xss_targets) xssTargetsResponse = convertDataToXSSTargetsResponse(data.xss_targets);
    });
    isLoading = false;
  }

</script>

<div class='flex flex-col gap-2'>
  <div class='flex flex-row items-center gap-2'>
    <h2 class="m-0 text-lg font-semibold">{isViewingToday ? 'Remaining Recovery' : 'Recovery After Last Activity'}</h2>
    <HelpTooltip message={isViewingToday
      ? 'This is your estimated recovery for each of your three systems based on your current training status, recovery demands, and recent activities.'
      : 'This is the estimated recovery for each of your three systems as of the end of the last activity on this day.'} />
  </div>
  {#if !recoveryDays}
    <Skeleton class="w-full h-[52px]" />
  {:else}
    <div class="grid grid-cols-4 gap-2 text-center">
      <div class="col-span-2 rounded bg-gray-50 p-2">
        <div class="font-medium text-primary">{formattedRecoveryTime.low.value} {formattedRecoveryTime.low.unit}</div>
        <div class="text-[var(--color-xlss)] text-sm">Low</div>
      </div>
      <div class="rounded bg-gray-50 p-2">
        <div class="font-medium text-primary">{formattedRecoveryTime.high.value} {formattedRecoveryTime.high.unit}</div>
        <div class="text-[var(--color-xhss)] text-sm">High</div>
      </div>
      <div class="rounded bg-gray-50 p-2">
        <div class="font-medium text-primary">{formattedRecoveryTime.peak.value} {formattedRecoveryTime.peak.unit}</div>
        <div class="text-[var(--color-xpss)] text-sm">Peak</div>
      </div>
    </div>
    <div class="text-sm text-muted-foreground text-wrap">
      Training status is <span class={cn('font-bold', getRecoveryColour($trainingAdvice?.form_cat || ''))}>{$trainingAdvice?.form_cat || 'N/A'}</span>{getRecoveryMessage($trainingAdvice?.form_cat || '', $trainingAdvice?.freshness_is_overridden || false)}
    </div>
  {/if}
  {#if !$trainingPlan?.isForecastPlan}
    <div class='flex flex-row gap-2 items-center'>
      <h3 class='m-0 text-base font-medium'>Freshness Feedback</h3>
      <HelpTooltip message='Feel differently from what the system is telling you? Select how you feel and your daily targets will adjust accordingly.' />
      {#if isLoading}
        <i class='fa-solid fa-spinner-third fa-spin'></i>
      {/if}
    </div>
    <div class='grid grid-cols-4 gap-2 text-center'>
      <Button class={cn('text-xs font-medium h-[28px] text-green-900 disabled:opacity-100', selectedFreshness === 'Very fresh' ? 'bg-green-400 hover:bg-green-400 -translate-y-1 shadow-lg' : 'bg-green-100 hover:bg-green-50', 'transition-all duration-150 ease-out transform-gpu will-change-transform')} on:click={() => updateFreshness('Very fresh')} disabled={selectedFreshness === 'Very fresh'}>
        Very Fresh
      </Button>
      <Button class={cn('text-xs font-medium h-[28px] text-blue-900 disabled:opacity-100', selectedFreshness === 'Fresh' ? 'bg-blue-400 hover:bg-blue-400 -translate-y-1 shadow-lg' : 'bg-blue-100 hover:bg-blue-50', 'transition-all duration-150 ease-out transform-gpu will-change-transform')} on:click={() => updateFreshness('Fresh')} disabled={selectedFreshness === 'Fresh'}>
        Fresh
      </Button>
      <Button class={cn('text-xs font-medium h-[28px] text-yellow-900 disabled:opacity-100', selectedFreshness === 'Tired' ? 'bg-yellow-400 hover:bg-yellow-400 -translate-y-1 shadow-lg' : 'bg-yellow-100 hover:bg-yellow-50', 'transition-all duration-150 ease-out transform-gpu will-change-transform')} on:click={() => updateFreshness('Tired')} disabled={selectedFreshness === 'Tired'}>
        Tired
      </Button>
      <Button class={cn('text-xs font-medium h-[28px] text-red-900 disabled:opacity-100', selectedFreshness === 'Very tired' ? 'bg-red-400 hover:bg-red-400 -translate-y-1 shadow-lg' : 'bg-red-100 hover:bg-red-50', 'transition-all duration-150 ease-out transform-gpu will-change-transform')} on:click={() => updateFreshness('Very tired')} disabled={selectedFreshness === 'Very tired'}>
        Very Tired
      </Button>
    </div>
    {#if $trainingAdvice?.freshness_is_overridden}
      <Button variant="link" size="sm" class="p-0 text-xs border-0 bg-transparent w-max h-6" on:click={() => updateFreshness(null)}>
        Clear Feedback
      </Button>
    {:else if !$trainingPlan?.isForecastPlan && +xssTargetsResponse?.completed.xss > 0 && selectedFreshness}
      <Button variant="link" size="sm" class="p-0 text-xs border-0 bg-transparent w-max h-6" on:click={() => updateFreshness(selectedFreshness === 'Detraining' ? 'Very fresh' : selectedFreshness)}>
        Apply Freshness to XSS Targets
      </Button>
    {/if}
  {/if}
</div>
